/**
 * The Navigator's catalog — one list of what a person can do in the product
 * (PRD-navigator-that-does-it FR-1). The Navigator panel, the Start screen and
 * the Harry and Wes prompts all read this list, and one matcher scores it.
 *
 * Two kinds of entry:
 *   - job   — a job in the workspace and the screen where it is done, with the
 *             ways to do it (modalities) and the answer to "how do I …".
 *             `routes` lists the pages where it is worth offering unasked.
 *   - start — a starting point: what a person is trying to do, in their words,
 *             and the job-shaped flow it lands in with a preset applied
 *             (PRD-start-intents §4). One want-case root per entry; the
 *             destination must work today (absence, not "coming soon").
 *
 * Every entry declares `terms` in the words people actually use. Pure data and
 * pure functions — no React, no stores — so the matcher is unit-testable and
 * the Start guide can run it on rails.
 */

import {
  BarChart3,
  Bot,
  Building2,
  FileSpreadsheet,
  Gauge,
  GitMerge,
  Inbox,
  MapPin,
  Network,
  Plus,
  Scale,
  ShoppingBag,
  Target,
  UserCheck,
  Users,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import type { AppAccent } from '../../apps/registry';

interface CatalogEntryBase {
  id: string;
  /** Short, verb-first title, the way a person would say it. */
  title: string;
  /** One line under the title. */
  summary: string;
  /** Words and phrases the matcher scores against; phrases score double. */
  terms: string[];
  icon: LucideIcon;
  /** Catch-all entries lose ties to specific ones. */
  generic?: boolean;
}

export interface JobModality {
  id: string;
  /** Verb-first label, e.g. "Dictate them aloud". */
  label: string;
  /** One line on what this modality does. */
  description: string;
  /** Where this modality lives. Omitted when comingSoon. */
  href?: string;
  /** Planned but not yet built — rendered disabled with a "Soon" tag. */
  comingSoon?: boolean;
}

export interface JobEntry extends CatalogEntryBase {
  kind: 'job';
  /** Where the job is done. */
  href: string;
  /** Ways to accomplish the job (offered as choices). */
  modalities?: JobModality[];
  /** Step-by-step answer to "how do I …". */
  howTo: string[];
  /** Route prefixes where this job is worth offering unasked. */
  routes: string[];
}

export type StartGroup = 'solo' | 'together' | 'plan' | 'org';

export const START_GROUP_LABELS: Record<StartGroup, string> = {
  solo: 'Just you',
  together: 'With others',
  plan: 'For the plan',
  org: 'For organizations',
};

/** What a starting point pre-decides for a Session-app destination. Consumed by
 *  NewSessionScreen via navigation state (accept-or-edit, never silent). */
export interface SessionPreset {
  /** Pre-filled session name; the user can edit before creating. */
  suggestedName?: string;
  /** What an option is called in this job ("candidate", "vendor", "topic"). */
  optionNoun?: string;
  /** Criteria suggestions rendered as accept-or-edit chips. */
  criteriaHints?: string[];
  /** Options extracted from the user's own words, confirmed before creation. */
  seedOptions?: string[];
}

export type StartDestination =
  | { kind: 'session'; preset: SessionPreset }
  | { kind: 'items' }
  | { kind: 'viability' }
  | { kind: 'session-import' }
  | { kind: 'cycle'; configurationSlug: string };

export interface StartEntry extends CatalogEntryBase {
  kind: 'start';
  /** Honest time-and-outcome line (replaces footprint jargon). */
  outcomeLine: string;
  group: StartGroup;
  /** Doc 15 provenance — the configuration this starting point descends from. */
  configurationSlug?: string;
  destination: StartDestination;
  accent: AppAccent;
}

export type CatalogEntry = JobEntry | StartEntry;

const JOB_ENTRIES: JobEntry[] = [

  {
    kind: 'job',
    id: 'add-items',
    title: 'Add items',
    summary: 'Capture one item or many — by voice, paste, form, or import.',
    terms: [
      'add', 'create', 'new', 'capture', 'enter', 'submit', 'log',
      'idea', 'ideas', 'problem', 'opportunity',
      'intake', 'bulk', 'many', 'multiple', 'batch', 'several', 'list of',
    ],
    icon: Plus,
    href: '/prioritization/scribe',
    modalities: [
      {
        id: 'dictate',
        label: 'Dictate them aloud',
        description: 'Speak freely — Scribe transcribes and extracts each item for you to review.',
        href: '/prioritization/scribe',
      },
      {
        id: 'paste',
        label: 'Paste free-form text',
        description: 'Paste notes or a rough list — Scribe splits it into structured item proposals.',
        href: '/prioritization/scribe',
      },
      {
        id: 'one-by-one',
        label: 'Add one at a time',
        description: 'Fill the intake form for a single, well-defined item.',
        href: '/prioritization/intake/submit',
      },
      {
        id: 'csv',
        label: 'Upload a CSV',
        description: 'Bulk-import many items from a spreadsheet column.',
        comingSoon: true,
      },
      {
        id: 'keyboard',
        label: 'Keyboard quick-add',
        description: 'Rapid-fire entry with keyboard shortcuts — type, Enter, repeat, no mouse.',
        comingSoon: true,
      },
    ],
    howTo: [
      'For a lot at once, the fastest path is Scribe: dictate or paste everything in one go and it extracts each item.',
      'For a single, carefully-defined item, use the intake form ("Add one at a time").',
      'Every proposal is shown to you for review before anything is created.',
    ],
    routes: [
      '/prioritization/dashboard',
      '/prioritization/items',
      '/prioritization/intake',
      '/prioritization/scribe',
    ],
  },
  {
    kind: 'job',
    id: 'classify-items',
    title: 'Classify items (category & altitude)',
    summary: 'Let the Air Traffic Controller assign a category and altitude 1–5.',
    terms: [
      'classify', 'classification', 'category', 'categorise', 'categorize',
      'altitude', 'level', 'air traffic', 'atc', 'tag', 'sort',
    ],
    icon: Bot,
    href: '/prioritization/settings/atc-agent',
    modalities: [
      {
        id: 'atc-batch',
        label: 'Run the Air Traffic Controller',
        description: 'Classify a batch of unclassified items, or auto-run on new items.',
        href: '/prioritization/settings/atc-agent',
      },
    ],
    howTo: [
      'The Air Traffic Controller (ATC) reads each item\'s title and description and assigns a best-fit category plus an altitude (1–5).',
      'Open the ATC agent to run it on a batch, or enable auto-run so new items are classified on creation.',
      'Every suggestion is a proposal — you confirm or override it.',
    ],
    routes: ['/prioritization/items', '/prioritization/intake', '/prioritization/settings'],
  },
  {
    kind: 'job',
    id: 'find-duplicates',
    title: 'Find duplicates & variants',
    summary: 'Twins clusters items that say the same thing in different words.',
    terms: [
      'duplicate', 'duplicates', 'dedupe', 'deduplicate', 'similar',
      'variant', 'variants', 'overlap', 'merge', 'twins', 'redundant',
    ],
    icon: GitMerge,
    href: '/prioritization/settings/twins-agent',
    howTo: [
      'Twins analyses items in batches and proposes clusters of duplicates (same problem, different wording) or variants on a theme.',
      'Run it from the Twins agent; review each proposed cluster and accept or dismiss it. Nothing is merged automatically.',
    ],
    routes: ['/prioritization/items', '/prioritization/settings'],
  },
  {
    kind: 'job',
    id: 'map-relationships',
    title: 'Map relationships & dependencies',
    summary: 'Nexus proposes how items depend on, break down, or roll up into each other.',
    terms: [
      'relationship', 'relationships', 'dependency', 'dependencies', 'depends',
      'block', 'blocks', 'blocked', 'link', 'links', 'connect', 'nexus', 'graph',
      'breakdown', 'rollup', 'package',
    ],
    icon: Network,
    href: '/prioritization/relationships/dependencies',
    howTo: [
      'Nexus reads your items and proposes structural relationships — dependencies, breakdowns, aggregations, and packages.',
      'Run a batch scan, or use the interactive advisor on an item to describe the link you have in mind in plain language.',
      'Proposed edges are reviewed before they are added to the graph.',
    ],
    routes: ['/prioritization/items', '/prioritization/relationships'],
  },
  {
    kind: 'job',
    id: 'score-items',
    title: 'Score & weigh items',
    summary: 'Apply criteria and values to rate items.',
    terms: [
      'score', 'scoring', 'rate', 'rating', 'weigh', 'weight', 'weights',
      'criteria', 'criterion', 'values', 'evaluate', 'assess',
    ],
    icon: Scale,
    href: '/prioritization/scoring',
    howTo: [
      'Open Scoring to evaluate items against your workspace criteria and weights.',
      'Scores feed the ranking; the Fracture agent can later stress-test how fragile that ranking is.',
    ],
    routes: ['/prioritization/scoring', '/prioritization/sessions'],
  },
  {
    kind: 'job',
    id: 'run-session',
    title: 'Run a prioritization session',
    summary: 'Facilitate a live group session — voting and ranking together.',
    terms: [
      'session', 'sessions', 'meeting', 'facilitate', 'vote', 'voting',
      'rank', 'ranking', 'workshop', 'live', 'group', 'dot voting', 'pairwise',
    ],
    icon: Target,
    href: '/prioritization/sessions',
    howTo: [
      'Open Sessions to start or join a live prioritization session.',
      'Pick a method (e.g. dot voting or pairwise) and the group ranks items together in real time.',
    ],
    routes: ['/prioritization/sessions', '/prioritization/scoring'],
  },
  {
    kind: 'job',
    id: 'triage-intake',
    title: 'Triage the intake queue',
    summary: 'Review, accept, or reject newly submitted items.',
    terms: [
      'triage', 'review', 'queue', 'inbox', 'approve', 'accept', 'reject',
      'incoming', 'submissions', 'pending',
    ],
    icon: Inbox,
    href: '/prioritization/intake/triage',
    howTo: [
      'Open the Triage queue to work through newly submitted items.',
      'For each one, accept it into the workspace, send it back for more work, or reject it.',
    ],
    routes: ['/prioritization/intake', '/prioritization/items', '/prioritization/dashboard'],
  },
  {
    kind: 'job',
    id: 'view-reports',
    title: 'See insights & reports',
    summary: 'Readiness, fragility, strategy gaps, and dependency leverage.',
    terms: [
      'report', 'reports', 'insight', 'insights', 'analytics', 'health',
      'fragile', 'fragility', 'fracture', 'cassandra', 'strategy gap', 'compass',
      'leverage', 'keystone', 'readiness', 'monitor', 'drift',
    ],
    icon: BarChart3,
    href: '/prioritization/reports/pipeline-health',
    howTo: [
      'Reports surface what a ranked list hides — item readiness, fragile rankings (Fracture), strategy gaps (Compass), and dependency leverage (Keystone).',
      'Start with Pipeline Health, then explore the intelligence agents from the Agents hub.',
    ],
    routes: ['/prioritization/reports', '/prioritization/monitor', '/prioritization/dashboard'],
  },
];

const START_ENTRIES: StartEntry[] = [
  {
    kind: 'start',
    id: 'decide-together',
    icon: Users,
    title: 'Decide something together',
    summary: 'Get the group to a defensible answer — options, what matters, votes, result.',
    outcomeLine: 'Set up in ~2 minutes — leave with a ranked result everyone saw made.',
    group: 'together',
    configurationSlug: 'team-meeting',
    terms: [
      'decide together', 'group decision', 'team decision', 'vote', 'we need to decide',
      'choose between', 'pick between', 'agree on', 'alignment', 'align', 'meeting',
      'decide', 'decision', 'stakeholders', 'consensus', 'weigh in',
    ],
    generic: true,
    destination: {
      kind: 'session',
      preset: { optionNoun: 'option' },
    },
    accent: 'mustard',
  },
  {
    kind: 'start',
    id: 'brain-dump',
    icon: Zap,
    title: 'Get it all out of my head',
    summary: 'Capture everything that might matter, fast. Organize it later.',
    outcomeLine: 'No setup — start typing (or dictating) immediately.',
    group: 'solo',
    configurationSlug: 'individual-brain-dump',
    terms: [
      'brain dump', 'out of my head', 'capture', 'write down', 'jot', 'list everything',
      'overwhelmed', 'too many things', 'ideas', 'backlog', 'collect', 'dump', 'inbox',
      'keep track', 'remember',
    ],
    destination: { kind: 'items' },
    accent: 'slate',
  },
  {
    kind: 'start',
    id: 'hire',
    icon: UserCheck,
    title: 'Pick who to hire',
    summary: 'Compare candidates against what actually matters for the role.',
    outcomeLine: 'Set up in ~2 minutes — leave with a defensible candidate ranking.',
    group: 'together',
    configurationSlug: 'hiring-talent',
    terms: [
      'hire', 'hiring', 'candidate', 'candidates', 'interview', 'recruit', 'recruiting',
      'offer', 'role', 'headcount decision', 'who to hire', 'talent', 'applicant',
    ],
    destination: {
      kind: 'session',
      preset: {
        suggestedName: 'Hiring decision',
        optionNoun: 'candidate',
        criteriaHints: ['Role fit', 'Evidence of impact', 'Growth trajectory', 'Team fit'],
      },
    },
    accent: 'mustard',
  },
  {
    kind: 'start',
    id: 'vendor',
    icon: ShoppingBag,
    title: 'Choose a vendor or partner',
    summary: 'Compare the contenders on the criteria that matter before you sign.',
    outcomeLine: 'Set up in ~2 minutes — leave with a ranked shortlist and the why.',
    group: 'together',
    configurationSlug: 'vendor-partner-selection',
    terms: [
      'vendor', 'vendors', 'partner', 'supplier', 'agency', 'contract', 'proposal',
      'rfp', 'tool selection', 'software selection', 'buy', 'procurement', 'crm',
      'platform choice', 'which tool',
    ],
    destination: {
      kind: 'session',
      preset: {
        suggestedName: 'Vendor selection',
        optionNoun: 'vendor',
        criteriaHints: ['Capability fit', 'Total cost', 'Risk', 'Support quality'],
      },
    },
    accent: 'mustard',
  },
  {
    kind: 'start',
    id: 'offsite',
    icon: MapPin,
    title: 'Plan the offsite',
    summary: 'Decide what the team will actually work on while you\'re together.',
    outcomeLine: 'Set up in ~2 minutes — everyone weighs in before or in the room.',
    group: 'together',
    configurationSlug: 'offsite-planning',
    terms: [
      'offsite', 'retreat', 'workshop', 'agenda', 'team week', 'planning day',
      'all hands', 'summit', 'kickoff',
    ],
    destination: {
      kind: 'session',
      preset: {
        suggestedName: 'Offsite priorities',
        optionNoun: 'topic',
        criteriaHints: ['Impact if we do it', 'Needs us in a room', 'Urgency'],
      },
    },
    accent: 'mustard',
  },
  {
    kind: 'start',
    id: 'focus',
    icon: Target,
    title: 'Focus the week',
    summary: 'Rank what\'s in front of you and commit to the top few.',
    outcomeLine: 'A few minutes, just you — leave with your short list.',
    group: 'solo',
    configurationSlug: 'personal-team-focus',
    terms: [
      'focus', 'this week', 'sprint', 'my priorities', 'what to work on',
      'where to spend', 'my time', 'attention', 'personal', 'quarter goals',
      'top three', 'most important',
    ],
    destination: {
      kind: 'session',
      preset: {
        suggestedName: 'This week\'s focus',
        optionNoun: 'item',
        criteriaHints: ['Moves the needle', 'Due soon', 'Only I can do it'],
      },
    },
    accent: 'slate',
  },
  {
    kind: 'start',
    id: 'import-results',
    icon: FileSpreadsheet,
    title: 'We already decided — put it on the record',
    summary: 'Import the results of a prioritization you ran somewhere else — items, ranking, scores.',
    outcomeLine: 'Drop the spreadsheet — leave with a real session on the record.',
    group: 'together',
    terms: [
      'import', 'spreadsheet', 'excel', 'csv', 'already decided', 'already ranked',
      'already prioritized', 'results', 'existing ranking', 'from the offsite',
      'workbook', 'bring in', 'upload', 'get it in', 'on the record',
    ],
    destination: { kind: 'session-import' },
    accent: 'green',
  },
  {
    kind: 'start',
    id: 'viability',
    icon: Gauge,
    title: 'Can we actually deliver this?',
    summary: 'Check the plan against capacity, then see when it can launch.',
    outcomeLine: 'Five rough-cut inputs — leave with a clear yes, no, or when.',
    group: 'plan',
    terms: [
      'deliver', 'capacity', 'feasible', 'feasibility', 'viability', 'launch',
      'timeline', 'can we do it', 'resourcing', 'bandwidth', 'realistic',
      'overcommitted', 'when can we ship', 'roadmap fit',
    ],
    destination: { kind: 'viability' },
    accent: 'green',
  },
  {
    kind: 'start',
    id: 'full-cycle',
    icon: Building2,
    title: 'Run a real prioritization cycle',
    summary: 'Governance, catchball, published priorities — the full platform.',
    outcomeLine: 'Honest setup: expect to configure workflow, people, and criteria.',
    group: 'org',
    configurationSlug: 'enterprise-prioritization-cycle',
    terms: [
      'cycle', 'governance', 'catchball', 'quarterly planning', 'annual planning',
      'strategic planning', 'portfolio', 'okr', 'budget allocation', 'resource allocation',
      'organization', 'company priorities', 'publish priorities', 'enterprise',
    ],
    generic: true,
    destination: { kind: 'cycle', configurationSlug: 'enterprise-prioritization-cycle' },
    accent: 'coral',
  },
];

/** Every entry: the jobs first, then the starting points. */
export const CATALOG: CatalogEntry[] = [...JOB_ENTRIES, ...START_ENTRIES];
export const JOBS: JobEntry[] = JOB_ENTRIES;
export const START_POINTS: StartEntry[] = START_ENTRIES;

export function getEntry(id: string): CatalogEntry | undefined {
  return CATALOG.find((e) => e.id === id);
}

export function getStartPoint(id: string): StartEntry | undefined {
  return START_ENTRIES.find((e) => e.id === id);
}

/** One line per entry, for a model that may answer with entry ids. */
export function catalogPromptLines(entries: CatalogEntry[] = CATALOG): string {
  return entries.map((e) => `- ${e.id}: ${e.title} — ${e.summary}`).join('\n');
}

// ── The matcher (the rails, and the whole behavior when AI is off) ───────────

export interface CatalogMatch<E extends CatalogEntry = CatalogEntry> {
  entry: E;
  score: number;
  /** The terms that fired — the deterministic "why". */
  matchedTerms: string[];
}

/** Title words that carry no signal: nearly every request is about items. */
const TITLE_STOP_WORDS = new Set([
  'a', 'an', 'the', 'to', 'i', 'my', 'we', 'it', 'all', 'out', 'of', 'on', 'or',
  'and', 'get', 'who', 'can', 'do', 'this', 'that', 'what', 'item', 'items',
]);

function normalize(text: string): string {
  return ` ${text.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim()} `;
}

/** Naive singular/plural stem: "vendors" and "vendor" score as one word. */
function stem(word: string): string {
  return word.length > 3 && word.endsWith('s') ? word.slice(0, -1) : word;
}

function titleWords(title: string): string[] {
  return normalize(title).trim().split(' ').filter((w) => w.length > 1 && !TITLE_STOP_WORDS.has(w));
}

/**
 * Score entries against what a person typed. A phrase term (containing a
 * space) found whole scores 2; a single word, or a word of the entry's title,
 * scores 1 and counts once per stem, across singular and plural. Ties go to
 * specific entries over generic catch-alls, then to catalog order. Returns
 * entries with score > 0, best first; empty input gives an empty list.
 */
export function matchCatalog<E extends CatalogEntry = CatalogEntry>(
  text: string,
  entries: E[] = CATALOG as E[],
): CatalogMatch<E>[] {
  const haystack = normalize(text);
  if (!haystack.trim()) return [];
  const words = new Set(haystack.trim().split(' ').filter((w) => !/^\d+$/.test(w)).map(stem));

  const matches: CatalogMatch<E>[] = [];
  for (const entry of entries) {
    let score = 0;
    const matchedTerms: string[] = [];
    const seenStems = new Set<string>();
    for (const term of [...entry.terms, ...titleWords(entry.title)]) {
      if (term.includes(' ')) {
        if (haystack.includes(` ${term} `)) {
          score += 2;
          matchedTerms.push(term);
        }
      } else {
        const termStem = stem(term);
        if (words.has(termStem) && !seenStems.has(termStem)) {
          seenStems.add(termStem);
          score += 1;
          matchedTerms.push(term);
        }
      }
    }
    if (score > 0) matches.push({ entry, score, matchedTerms });
  }
  return matches.sort(
    (a, b) =>
      b.score - a.score ||
      Number(a.entry.generic ?? false) - Number(b.entry.generic ?? false),
  );
}

/** Jobs worth offering unasked on a page, matched by route prefix, in catalog order. */
export function jobsForRoute(pathname: string, jobs: JobEntry[] = JOB_ENTRIES): JobEntry[] {
  return jobs.filter((j) => j.routes.some((route) => pathname === route || pathname.startsWith(route)));
}
